import { useTranslations } from '@brightdrop/localization-client';
import type { SupportedLocale } from '@brightdrop/localization-core';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '~/app/store';
import { setCurrentLocale } from '~/store/profile/profileSlice';
import {
  selectCurrentLocale,
  selectProfileUserPreferences,
} from '~/store/profile/profileSlice.selectors';

/**
 * Keeps the app language in i18next and the profile slice's `currentLocale`
 * in step. A profile locale wins once one has loaded; before that the slice
 * starts from the language i18next chose at startup.
 *
 * Past that seed, the slice is the source of truth and i18next only follows
 * it. Copying i18next back into the slice on every change would race the
 * effect that applies the slice: when both change in one render, each effect
 * copies the other's stale value and the two swap forever.
 *
 * A language set from the browser console outranks the profile locale while it applies.
 *
 * @param override - The console language, or null when none applies
 */
const useAppLocaleSync = (override: SupportedLocale | null = null): void => {
  const { instance, updateLocale } = useTranslations();
  const dispatch = useAppDispatch();
  const locale = useSelector(selectCurrentLocale);
  const userPreferences = useSelector(selectProfileUserPreferences);
  const targetLocale = override ?? userPreferences?.locale;

  // biome-ignore lint/correctness/useExhaustiveDependencies: seeds the slice from i18next only at startup and when a profile locale stops applying; following `instance.language` would reintroduce the swap described above
  useEffect(() => {
    // Startup already chose the language picked before sign-in, else the
    // cookie, else the browser language. A profile locale replaces it below.
    if (targetLocale) {
      return;
    }
    dispatch(setCurrentLocale(instance.language));
  }, [dispatch, targetLocale]);

  // A profile refresh writes the account locale back into the slice even while i18next already
  // speaks the console language, so the slice is returned to the override on its own.
  useEffect(() => {
    if (override && override !== locale) {
      dispatch(setCurrentLocale(override));
    }
  }, [dispatch, locale, override]);

  useEffect(() => {
    if (targetLocale && targetLocale !== instance.language) {
      dispatch(setCurrentLocale(targetLocale));
      if (override) {
        // `updateLocale` holds pseudo-localization (`en-XA`) in place, and a language asked for
        // from the console has to replace it.
        void instance.changeLanguage(override);
      } else {
        updateLocale(targetLocale);
      }
      return;
    }
    if (!targetLocale && locale) {
      updateLocale(locale);
    }
  }, [
    dispatch,
    instance,
    instance.language,
    locale,
    override,
    targetLocale,
    updateLocale,
  ]);
};

export default useAppLocaleSync;
